/**
 * openTrustedUrl — open a backend-supplied URL in a new browser tab, safely.
 *
 * URLs returned by callables / HTTP endpoints (signed report links, billing
 * documents) are data, not code. Handing them straight to `window.open` would
 * execute `javascript:` or `data:` URLs if the backend were ever compromised
 * or misconfigured (Aikido SAST finding, PLA-514).
 *
 * Rules:
 * - absolute `https:` URLs only (no host allowlist — the backend hands out
 *   HubSpot signed URLs whose CDN hostnames vary);
 * - always `noopener,noreferrer`, so the opened tab cannot reach back into
 *   `window.opener`.
 *
 * Web-only: callers must guard with `Platform.OS === "web"`.
 */
export function isTrustedHttpsUrl(url: unknown): url is string {
  if (typeof url !== "string" || url.length === 0) return false
  try {
    return new URL(url).protocol === "https:"
  } catch {
    return false
  }
}

/**
 * Opens `url` in a new tab. Returns `false` (and opens nothing) when the URL
 * is not an absolute `https:` URL or when no `window` is available.
 */
export function openTrustedUrl(url: unknown): boolean {
  if (!isTrustedHttpsUrl(url)) return false
  if (typeof window === "undefined" || typeof window.open !== "function") return false
  window.open(url, "_blank", "noopener,noreferrer")
  return true
}
